"use client";
import { Track } from "@/src/lib/tracking";

import { Icon, OspToast, ospThemes } from "@ospweb/sdk-ui";
import { useAppPurchaseFlow } from "@hooks/useAppPurchaseFlow";
import { useMemberFeature } from "@hooks/useMemberFeature";
import { useMemberUser } from "@hooks/useMemberUser";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import {
  ProductPayCycle,
  ProductStatus,
  ProductType,
} from "@components/Member/CurrentPlan/constants";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { I18nUtil } from "@utils/i18n";
import { AppImage } from "@components/NextImage";
import { MemberKeys } from "@screens/Member";
import { MemberProductLevelEnum, Opportunity } from "@features/api/memberApi";
import { QUERY_KEYS } from "@config/constant";
import { ROUTER_PATH } from "@config/router";
import Translations from "@translate/components/AlphaPick/AlphaPickPricing/RetainModalContent/RetainModalContent";
import { cn } from "utils";
import { useQueryClient } from "@tanstack/react-query";

const I18n = I18nUtil.createScoped(Translations);

const MEMBER_INVALIDATE_QUERY_KEYS = [
  [QUERY_KEYS.MEMBER_FEATURE],
  [QUERY_KEYS.MEMBER_USE_PLAN],
  [QUERY_KEYS.MEMBER_RETENTION_STATS],
  [QUERY_KEYS.MEMBER_FEATURE_EQUITYLIST],
  [QUERY_KEYS.PAYMENT_LIST_PAYMENT],
  [QUERY_KEYS.MEMBER_EQUITY_LIST],
] as const;

type RetainPurchaseDecision = {
  buttonTitle: string;
  buttonDisabled: boolean;
  canPurchase: boolean;
  hideButton?: boolean;
};

type RetainAvailableProductAction =
  "PURCHASE" | "RENEW" | "UPGRADE_TO_LATEST" | string;

const PURCHASE_ENABLED_ACTIONS = new Set<RetainAvailableProductAction>([
  "PURCHASE",
  "RENEW",
  "UPGRADE_TO_LATEST",
  "UPGRADE",
]);

export interface RetainModalContenttProps {
  onClose?: () => void;
  /**
   * 导航类动作（跳定价页）的关闭动作；未传时回退 onClose。
   * 与 `PaywallModalContent` 同约定：不执行调用方 onClose 的 dismiss 副作用，避免同 tick 双导航。
   */
  onNavigateAway?: () => void;
  isPc?: boolean;
  routerType?: string;
  opportunity?: Opportunity;
  credits?: number;
  trackingModuleName?: string;
  trackingSymbol?: string;
  retainModalCopyOverrides?: {
    title?: string;
    subtitle?: string;
    features?: string[];
    userData?: Array<{ value: number; label: string }>;
  };
}

const EdCompleteAnnualCard = ({
  annualPrice,
  className,
}: {
  annualPrice?: number;
  className?: string;
}) => (
  <div
    className={cn(
      "w-full rounded-2 border-2 border-border_accent4_solid bg-background_accent4_overlay10 p-3",
      className,
    )}
  >
    <p className="title_m text-content_primary">
      {I18n.t("Ed Complete Annual")}
    </p>
    <div className="mt-3 flex flex-wrap items-end gap-1">
      <span className="headline_regular_l text-content_accent4_primary">
        ${annualPrice != null ? annualPrice.toFixed(2) : "--"}
      </span>
      {annualPrice != null && (
        <span className="body_s text-content_secondary">
          {I18n.t("(≈$%{var0}/mo)", {
            var0: (annualPrice / 12).toFixed(2),
          })}
        </span>
      )}
    </div>
  </div>
);

export const RetainModalContent: React.FC<RetainModalContenttProps> = ({
  onClose,
  onNavigateAway,
  isPc = false,
  retainModalCopyOverrides,
}) => {
  const router = useShellRouter();
  const queryClient = useQueryClient();
  const { data: memberInfo, refetch: refetchMemberUser } = useMemberUser();
  const { data: memberProductData } = useMemberFeature();
  const { purchaseInApp, shouldTryNativePayment } = useAppPurchaseFlow();
  const title = retainModalCopyOverrides?.title;
  const subtitle = retainModalCopyOverrides?.subtitle;
  const featureItems = useMemo(
    () =>
      (retainModalCopyOverrides?.features || [])
        .slice(0, 3)
        .map((text) => ({ text })),
    [retainModalCopyOverrides?.features],
  );
  const userData = retainModalCopyOverrides?.userData ?? [];
  const [isAppPayingRenewLoading, setIsAppPayingRenewLoading] = useState(false);

  const completeYearProduct = useMemo(() => {
    const yearProducts = (memberProductData || []).filter(
      (item) =>
        item.pay_cycle === ProductPayCycle.YEAR &&
        item.status === ProductStatus.ACTIVE &&
        item.type === ProductType.NORMAL &&
        item.level === MemberProductLevelEnum.EXPERT,
    );
    return yearProducts[0];
  }, [memberProductData]);

  const annualDisplayPrice = Number(completeYearProduct?.price ?? 0);

  const purchaseDecision = useMemo<RetainPurchaseDecision>(() => {
    const selectedProduct = completeYearProduct;
    if (!selectedProduct?.id) {
      return {
        buttonTitle: I18n.t("Contact Us"),
        buttonDisabled: true,
        canPurchase: false,
        hideButton: true,
      };
    }

    const autoRenewal = memberInfo?.auto_renewal;
    const renewalId = memberInfo?.renewal_id;
    const isUserTrial = memberInfo?.isTrial;
    const availableProducts = memberInfo?.available_products || [];

    const isPayingPlan = Boolean(
      isUserTrial && renewalId === selectedProduct.id,
    );
    const isCurrentPlan =
      memberInfo?.product?.id === selectedProduct.id || isPayingPlan;

    if (isCurrentPlan && autoRenewal) {
      return {
        buttonTitle: I18n.t("Current Plan"),
        buttonDisabled: true,
        canPurchase: false,
        hideButton: false,
      };
    }

    const matchedAvailable = availableProducts.find(
      (item) => item.product_id === selectedProduct.id,
    );

    if (!matchedAvailable) {
      return {
        buttonTitle: I18n.t("Contact Us"),
        buttonDisabled: true,
        canPurchase: false,
        hideButton: true,
      };
    }

    if (PURCHASE_ENABLED_ACTIONS.has(matchedAvailable.action || "")) {
      return {
        buttonTitle: I18n.t("Renew"),
        buttonDisabled: false,
        canPurchase: true,
        hideButton: false,
      };
    }

    return {
      buttonTitle: I18n.t("Contact Us"),
      buttonDisabled: true,
      canPurchase: false,
      hideButton: true,
    };
  }, [completeYearProduct, memberInfo]);

  const goToPricingPage = useCallback(() => {
    // 导航而非 dismiss：不能走 onClose，否则调用方的 dismiss 副作用（router.push 回列表）
    // 会与下面这次 push 形成同 tick 双导航
    (onNavigateAway ?? onClose)?.();
    router.push(
      `${ROUTER_PATH.MEMBER}/?tab=${MemberKeys.PRICING}&planId=${completeYearProduct?.id ?? ""}`,
    );
  }, [completeYearProduct?.id, onNavigateAway, onClose, router]);

  const invalidateMemberQueries = useCallback(async () => {
    await Promise.all(
      MEMBER_INVALIDATE_QUERY_KEYS.map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  }, [queryClient]);

  const onRenewSuccess = useCallback(async () => {
    if (shouldTryNativePayment) {
      const maxAttempts = 5;
      const intervalMs = 1000;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const result = await refetchMemberUser();
        const latestMemberInfo = result?.data;
        const hasRenewedCurrentSelection =
          latestMemberInfo?.auto_renewal &&
          (latestMemberInfo?.product?.renewal_id === completeYearProduct?.id ||
            latestMemberInfo?.product?.id === completeYearProduct?.id);

        if (hasRenewedCurrentSelection) {
          break;
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }
    }

    onClose?.();
    await invalidateMemberQueries();
  }, [
    completeYearProduct?.id,
    invalidateMemberQueries,
    onClose,
    refetchMemberUser,
    shouldTryNativePayment,
  ]);

  const handleSubscribeClick = useCallback(async () => {
    if (isAppPayingRenewLoading || purchaseDecision.buttonDisabled) {
      return;
    }

    if (!completeYearProduct || !purchaseDecision.canPurchase) {
      goToPricingPage();
      return;
    }

    if (shouldTryNativePayment) {
      setIsAppPayingRenewLoading(true);
      const result = await purchaseInApp(completeYearProduct, {
        onSuccess: () => {
          onRenewSuccess().finally(() => setIsAppPayingRenewLoading(false));
        },
        onError: (msg) => {
          OspToast.error({
            content: msg || I18n.t("Purchase failed."),
          });
        },
        onMemberExist: async () => {
          await invalidateMemberQueries();
        },
        suppressTimeoutToast: true,
      });

      if (!result.success) {
        setIsAppPayingRenewLoading(false);
        if (result.fallbackToStripe) {
          goToPricingPage();
        } else {
          OspToast.error({
            content: result.error || I18n.t("Purchase failed."),
          });
        }
      }
      return;
    }

    goToPricingPage();
  }, [
    completeYearProduct,
    goToPricingPage,
    invalidateMemberQueries,
    isAppPayingRenewLoading,
    onRenewSuccess,
    purchaseDecision.buttonDisabled,
    purchaseDecision.canPurchase,
    purchaseInApp,
    shouldTryNativePayment,
  ]);

  useEffect(() => {
    if (completeYearProduct) {
      // 扁平标量（规范 §3.2）—— product 是对象，拆成字段
      Track.view("alpha_pick_pricing_retain_modal_view", {
        product_id: completeYearProduct.id,
        product_name: completeYearProduct.name,
        product_price: completeYearProduct.price,
        pay_cycle: completeYearProduct.pay_cycle,
      });
    }
  }, [completeYearProduct]);

  return (
    <div className="relative flex h-full w-full flex-col bg-background_solid_neutral_secondary">
      <button
        type="button"
        aria-label="close"
        className={cn(
          "absolute z-20 cursor-pointer",
          isPc ? "left-4 top-4" : "left-4 fixed-top-safe py-3",
        )}
        onClick={onClose}
      >
        <Icon
          data-openreplay-label="investmoves_retainmodalcontent_close"
          name="close"
          size={24}
          color={ospThemes.colors.content_primary}
        />
      </button>

      <div className="z-10 h-full overflow-y-auto overflow-x-hidden no-scrollbar">
        <div
          className={cn(
            "flex min-h-full flex-col justify-between",
            isPc ? "pt-6" : "pt-safe",
            isPc ? "pb-3" : "pb-safe",
          )}
        >
          <div
            className={cn("flex flex-col gap-6 px-4", isPc ? "mt-0" : "mt-12")}
          >
            <div className="flex flex-col items-center gap-2">
              <AppImage
                src="/assets/images/smile.svg"
                alt="retain"
                width={64}
                height={64}
                loading="eager"
              />
              <div className="flex flex-col gap-2 text-center">
                <div className="headline_regular_l text-content_primary">
                  {title}
                </div>
                <div className="body_m text-content_secondary">{subtitle}</div>
              </div>
            </div>

            {userData.length > 0 && (
              <div className="flex flex-row justify-between gap-2 rounded-2 bg-background_solid_neutral_primary p-3">
                {userData.map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-1 flex-col items-center"
                  >
                    <span className="headline_regular_s text-content_primary">
                      {item.value}
                    </span>
                    <span className="body_s text-center text-content_tertiary">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {featureItems.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="body_medium_l text-content_primary">
                  {I18n.t("What Ed handled for you")}
                </div>
                <div className="flex flex-col gap-2">
                  {featureItems.map((f) => (
                    <div key={f.text} className="flex items-start gap-2">
                      <Icon
                        name="check"
                        size={16}
                        className="mt-1 shrink-0 text-content_brand_primary"
                      />
                      <span className="body_l text-content_secondary">
                        {f.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col">
            <div className="px-4 py-6">
              <EdCompleteAnnualCard annualPrice={annualDisplayPrice} />
            </div>

            <div className="bg-background_solid_neutral_secondary px-4 pb-safe">
              {!purchaseDecision.hideButton ? (
                <button
                  aria-label="Retain Modal Content"
                  data-openreplay-label="investmoves_retainmodalcontent_subscribe"
                  id="btn-retain-modal-content"
                  type="button"
                  className="button button_m primary_button h-11 w-full rounded-2 bg-background_accent4_overlay100 text-content_inverse_primary"
                  data-loading={isAppPayingRenewLoading}
                  data-tracking-eventname="alpha_pick_pricing_retain_modal_subscribe_click"
                  data-tracking-event-key-to_product={completeYearProduct?.name}
                  data-tracking-event-key-to_price={completeYearProduct?.price}
                  data-tracking-event-key-to_pay_cycle={
                    completeYearProduct?.pay_cycle
                  }
                  onClick={handleSubscribeClick}
                  disabled={
                    !completeYearProduct ||
                    isAppPayingRenewLoading ||
                    purchaseDecision.buttonDisabled
                  }
                >
                  {purchaseDecision.buttonTitle}
                </button>
              ) : (
                <div className="h-11" />
              )}

              <button
                type="button"
                className="button_m text_button h-11 w-full text-content_tertiary"
                onClick={goToPricingPage}
              >
                {I18n.t("View all plans")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
